import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { WHATSAPP_URL } from "@/lib/svenData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Five short questions, each answered by picking one chip. Mirrors the
// original "Ask Sven" quiz — kept intentionally short for a pop-up.
const QUESTIONS = [
  { id: "silhouette", num: "01", title: "What kind of silhouette attracts you?", options: ["Clean and classic", "Relaxed and soft", "Strong and structured", "More directional"] },
  { id: "garment", num: "02", title: "What are you considering?", options: ["Single-breasted suit", "Double-breasted suit", "Three-piece suit", "Jacket & trousers separately"] },
  { id: "fabric", num: "03", title: "Which cloth interests you?", options: ["Linen & breathable cloth", "Fine wool & classic suiting", "Texture & expressive cloth", "Not sure yet"] },
  { id: "occasion", num: "04", title: "Where will you wear it?", options: ["Work & everyday dressing", "Dinners & evenings", "Weddings & celebrations", "Building a wardrobe"] },
  { id: "priority", num: "05", title: "What matters most?", options: ["Fit & proportion", "Fabric & texture", "Design & individuality", "Versatility"] },
];

const chipCls = (active) =>
  `px-3.5 py-2 rounded-full border font-mono-ov text-[10px] tracking-[0.1em] uppercase transition-all duration-200 ${
    active ? "bg-[#141414] text-[#faf8f5] border-[#141414]" : "bg-white text-[#57534e] border-[#e7e2da] hover:border-[#c5a059]"
  }`;

const inputCls = "w-full bg-white border border-[#e7e2da] rounded-sm px-4 py-3 text-sm font-light text-[#141414] placeholder:text-[#a8a29a] focus:outline-none focus:border-[#c5a059] transition-colors";

// The modal itself — controlled, so it can be mounted once and opened from
// anywhere (nav, hero, this page, etc). Use <StyleQuizTrigger /> below for a
// self-contained button + modal pair that needs no state wiring.
export const StyleQuizModal = ({ open, onOpenChange }) => {
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({ name: "", phone: "", email: "" });
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const pick = (qid, val) => setAnswers((a) => ({ ...a, [qid]: val }));
  const setC = (k) => (e) => setContact((c) => ({ ...c, [k]: e.target.value }));
  const allAnswered = QUESTIONS.every((q) => answers[q.id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!allAnswered) {
      toast.error("Please answer all five questions.");
      return;
    }
    if (!contact.name.trim() || !contact.phone.trim()) {
      toast.error("Please add your name and phone number.");
      return;
    }
    setSending(true);
    // The leads schema only stores name/phone/service/source today, so the
    // quiz answers (and any note) are folded into `service` as a readable
    // summary rather than dropped — extend the backend with a `notes` field
    // if you'd rather keep these structured.
    const summary = QUESTIONS.map((q) => answers[q.id]).join(" · ");
    const service = `Style Quiz — ${summary}${message.trim() ? ` · Note: ${message.trim()}` : ""}`;
    try {
      await axios.post(`${API}/leads`, {
        name: contact.name,
        phone: contact.phone,
        service,
        source: "style-quiz",
      });
    } catch (err) {
      console.error("lead save failed", err);
    }

    // Same WhatsApp-redirect pattern as WhatsAppModal in Booking.jsx — the
    // brief actually reaches the team instead of vanishing into a console.log.
    const waMsg = [
      "Hello Sven Suits! I'd like a style consultation.",
      `Name: ${contact.name}`,
      `Phone: ${contact.phone}`,
      contact.email.trim() && `Email: ${contact.email.trim()}`,
      "—",
      ...QUESTIONS.map((q) => `${q.title} ${answers[q.id]}`),
      message.trim() && `Note: ${message.trim()}`,
    ].filter(Boolean).join("\n");
    window.open(`${WHATSAPP_URL}?text=${encodeURIComponent(waMsg)}`, "_blank");
    toast.success("Opening WhatsApp with your style brief…");

    setSending(false);
    setDone(true);
  };

  const reset = () => {
    setAnswers({});
    setContact({ name: "", phone: "", email: "" });
    setMessage("");
    setDone(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent data-testid="style-quiz-modal" className="bg-[#faf8f5] border-[#e7e2da] sm:max-w-lg max-h-[85vh] overflow-y-auto">
        {done ? (
          <div data-testid="style-quiz-success" className="py-4 flex gap-4">
            <CheckCircle2 className="text-[#c5a059] flex-shrink-0 mt-0.5" size={24} />
            <div>
              <DialogTitle className="font-display text-2xl font-medium text-[#141414]">We have your brief.</DialogTitle>
              <p className="mt-2 text-sm font-light leading-relaxed text-[#57534e]">
                Continue the conversation in the WhatsApp tab we just opened for you — the team
                will review your answers and come back with a considered direction.
              </p>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="mt-5 font-mono-ov text-[11px] tracking-[0.15em] uppercase text-[#99732b] hover:text-[#141414] transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-medium text-[#141414]">Tell us what you're trying to solve.</DialogTitle>
              <DialogDescription className="font-light text-[#57534e]">
                You don't need to know the name of the style — pick what feels closest.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="space-y-6 mt-2">
              {QUESTIONS.map((q) => (
                <div key={q.id}>
                  <p className="font-mono-ov text-[10px] tracking-[0.15em] uppercase text-[#57534e] mb-2.5">
                    <span className="text-[#c5a059]">{q.num}</span> — {q.title}
                  </p>
                  <div className="flex flex-wrap gap-2" data-testid={`style-quiz-q-${q.id}`}>
                    {q.options.map((opt) => (
                      <button
                        type="button"
                        key={opt}
                        data-testid={`style-quiz-option-${q.id}`}
                        onClick={() => pick(q.id, opt)}
                        className={chipCls(answers[q.id] === opt)}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              <textarea
                data-testid="style-quiz-message"
                placeholder="Anything else we should know? (occasion, a reference you've saved, a garment you love…)"
                rows={3}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className={inputCls}
              />

              <div className="grid sm:grid-cols-2 gap-3">
                <input data-testid="style-quiz-name" required placeholder="Your name" value={contact.name} onChange={setC("name")} className={inputCls} />
                <input data-testid="style-quiz-phone" required placeholder="WhatsApp / phone" value={contact.phone} onChange={setC("phone")} className={inputCls} />
              </div>
              <input data-testid="style-quiz-email" type="email" placeholder="Email (optional)" value={contact.email} onChange={setC("email")} className={inputCls} />

              <button
                type="submit"
                data-testid="style-quiz-submit-btn"
                disabled={sending}
                className="w-full py-3.5 bg-[#141414] text-[#faf8f5] font-mono-ov text-xs tracking-[0.15em] uppercase rounded-full hover:bg-[#c5a059] hover:text-[#0b0b0c] transition-all duration-300 disabled:opacity-60"
              >
                {sending ? "Sending…" : "Send to Sven"}
              </button>
              <p className="text-xs font-light text-[#57534e]/80 text-center">
                We don't auto-assign a style — the team reviews every answer before recommending a direction.
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Self-contained drop-in: renders its own trigger button and owns the open
// state, so it can be placed anywhere with zero wiring, e.g.
// <StyleQuizTrigger label="Not sure what suits you?" />
export const StyleQuizTrigger = ({ label = "Take the Style Quiz", testId = "style-quiz-trigger", className }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        data-testid={testId}
        onClick={() => setOpen(true)}
        className={className || "px-8 py-3.5 bg-[#c5a059] text-[#0b0b0c] font-mono-ov text-xs tracking-[0.18em] uppercase rounded-full hover:bg-[#dfcca4] hover:scale-[1.03] transition-all duration-300"}
      >
        {label}
      </button>
      <StyleQuizModal open={open} onOpenChange={setOpen} />
    </>
  );
};
